import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Shell from "../components/Shell.jsx";
import { cachedGet } from "../lib/outbox.js";
import { centsToDisplay } from "../lib/money.js";
import { Segmented, inputCls } from "../components/ui.jsx";

const FILTERS = [
  { value: "", label: "All" },
  { value: "expense", label: "Expenses" },
  { value: "income", label: "Income" },
];

const TYPE_BADGE = {
  income: "text-emerald-600",
  expense: "text-slate-800",
  owner_draw: "text-amber-600",
  owner_contribution: "text-sky-600",
};

function sign(t) {
  return t.type === "income" || t.type === "owner_contribution" ? "+" : "−";
}

export default function Ledger() {
  const [txns, setTxns] = useState(null);
  const [totals, setTotals] = useState(null);
  const [q, setQ] = useState("");
  const [type, setType] = useState("");
  const [stale, setStale] = useState(false);
  // The list is a page, not the books. Without this the screen showed the
  // newest hundred entries and gave no sign there was anything behind them —
  // the operator went looking for a 2025 row and hit a wall at April 2026.
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const query = (offset) => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (type) params.set("type", type);
    if (offset) params.set("offset", String(offset));
    return `/api/transactions?${params}`;
  };

  useEffect(() => {
    const t = setTimeout(() => {
      cachedGet(query(0))
        .then(({ data, stale: s }) => {
          setTxns(data.transactions);
          setTotals(data.totals || null);
          setHasMore(!!data.has_more);
          setStale(s);
        })
        .catch(() => setTxns([]));
    }, q ? 250 : 0);
    return () => clearTimeout(t);
  }, [q, type]);

  async function loadMore() {
    setLoadingMore(true);
    try {
      const { data } = await cachedGet(query(txns.length));
      setTxns((prev) => [...prev, ...(data.transactions || [])]);
      setHasMore(!!data.has_more);
    } catch {
      setHasMore(false);
    } finally {
      setLoadingMore(false);
    }
  }

  const byDate = useMemo(() => {
    const groups = new Map();
    for (const t of txns || []) {
      if (!groups.has(t.date)) groups.set(t.date, []);
      groups.get(t.date).push(t);
    }
    return [...groups.entries()];
  }, [txns]);

  return (
    <Shell title="Ledger">
      <input
        type="search"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search vendor or memo…"
        className={inputCls}
      />
      <Segmented options={FILTERS} value={type} onChange={setType} />

      {totals && totals.count > 0 && (
        <section className="rounded-2xl bg-white px-4 py-3 shadow-sm">
          {type === "" ? (
            <div className="flex items-center justify-between text-sm">
              <span>
                <span className="font-semibold text-emerald-600">+{centsToDisplay(totals.income_cents)}</span>
                <span className="mx-1.5 text-slate-300">·</span>
                <span className="font-semibold text-slate-700">−{centsToDisplay(totals.expense_cents)}</span>
              </span>
              <span className="text-xs text-slate-400">
                Net{" "}
                <span className={`text-sm font-semibold ${totals.income_cents - totals.expense_cents >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                  {centsToDisplay(totals.income_cents - totals.expense_cents)}
                </span>
              </span>
            </div>
          ) : (
            <div className="flex items-center justify-between text-sm">
              <span className={`font-semibold ${type === "income" ? "text-emerald-600" : "text-slate-700"}`}>
                {type === "income" ? "+" : "−"}
                {centsToDisplay(type === "income" ? totals.income_cents : totals.expense_cents)}
              </span>
              <span className="text-xs text-slate-400">
                {totals.count} entr{totals.count === 1 ? "y" : "ies"}
                {q ? " matching" : ""}
              </span>
            </div>
          )}
          {txns && totals.count > txns.length && (
            <p className="mt-1 text-xs text-slate-400">
              Showing {txns.length} of {totals.count} — the oldest are below.
            </p>
          )}
        </section>
      )}

      {stale && (
        <p className="rounded-xl bg-slate-100 px-4 py-2 text-center text-xs text-slate-500">
          Offline — showing last synced data
        </p>
      )}
      {txns === null && <p className="text-center text-sm text-slate-400">Loading…</p>}
      {txns?.length === 0 && (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center">
          <p className="text-4xl">📒</p>
          <p className="mt-3 font-medium text-slate-700">No entries{q || type ? " match" : " yet"}</p>
          {!q && !type && (
            <Link to="/add" className="mt-2 inline-block text-sm font-semibold text-navy-700">
              Add your first entry →
            </Link>
          )}
        </div>
      )}

      {byDate.map(([date, list]) => (
        <section key={date}>
          <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">
            {new Date(`${date}T12:00:00`).toLocaleDateString("en-US", {
              weekday: "short",
              month: "short",
              day: "numeric",
            })}
          </p>
          <div className="divide-y divide-slate-100 rounded-2xl bg-white shadow-sm">
            {list.map((t) => (
              <Link key={t.id} to={`/edit/${t.id}`} className="flex items-center justify-between px-4 py-3">
                <div className="min-w-0">
                  <p className="truncate font-medium text-slate-800">{t.vendor || t.account_name}</p>
                  <p className="truncate text-xs text-slate-400">
                    {t.account_name}
                    {t.business_use_pct < 100 ? ` · ${t.business_use_pct}% business` : ""}
                    {t.memo ? ` · ${t.memo}` : ""}
                  </p>
                </div>
                <p className={`ml-3 shrink-0 font-semibold ${TYPE_BADGE[t.type] || ""}`}>
                  {sign(t)}
                  {centsToDisplay(t.amount_cents)}
                </p>
              </Link>
            ))}
          </div>
        </section>
      ))}

      {hasMore && (
        <button
          onClick={loadMore}
          disabled={loadingMore}
          className="w-full rounded-xl border border-navy-600/30 bg-navy-700/5 py-3 text-sm font-semibold text-navy-700 disabled:opacity-50"
        >
          {loadingMore ? "Loading…" : "Load older entries"}
        </button>
      )}
    </Shell>
  );
}
